import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleAlert, ExternalLink, KeyRound, Loader2, Lock, RefreshCw, ShieldCheck, Undo2, Webhook } from "lucide-react";
import { api, extractErr, usePortalAuth } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";
import { money } from "@/components/PriceBreakdown";

const eyebrow = "text-xs uppercase tracking-[0.2em] font-medium text-[#8A9A86]";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const Ok = ({ ok }) => (ok ? <CheckCircle2 className="w-4 h-4 text-[#4A6A48] shrink-0 mt-0.5" /> : <CircleAlert className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" />);

export default function StripeConnection() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const load = async () => {
    try { setS((await api.get("/admin/stripe/connection")).data); setErr(""); } catch (e) { setErr(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);
  const run = async (name, fn, ok) => {
    setBusy(name);
    try { const { data } = await fn(); if (data?.env_var) setS(data); else await load(); if (ok) toast.success(ok); return data; }
    catch (e) { toast.error(extractErr(e)); return null; } finally { setBusy(""); }
  };
  if (err && !s) return <p data-testid="stripe-load-error" className="text-sm text-[#B45309]">{err}</p>;
  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-[#78716C]" />;
  const own = s.key_source === "portal" || s.key_source === "managed";
  return (
    <div className="space-y-4" data-testid="stripe-connection-panel">
      <ConnectionStatus s={s} onRefresh={load} />
      {s.key_source !== "portal" && <ManagedConnector s={s} />}
      {own && (
        <>
          <VerifyPanel s={s} busy={busy} run={run} />
          {s.mode === "test" && <TestCheckout s={s} busy={busy} run={run} />}
        </>
      )}
      <GoLiveChecklist refreshKey={s.connected_at + (s.last_verification?.checked_at || "") + s.key_source} />
      <SettingsGuide live={s.mode === "live"} />
      <FallbackSection s={s} busy={busy} run={run} />
    </div>
  );
}

const STATES = {
  unclaimed: { label: "Awaiting claim", tone: "bg-[#FEF3C7] text-[#92400E]", step: 1 },
  claimed: { label: "Claimed", tone: "bg-[#DBEAFE] text-[#1E40AF]", step: 2 },
  account_activated: { label: "Account activated", tone: "bg-[#DCFCE7] text-[#166534]", step: 3 },
  app_installed_in_live_mode: { label: "Live keys installed", tone: "bg-[#DCFCE7] text-[#166534]", step: 4 },
  expired: { label: "Expired", tone: "bg-[#FEE2E2] text-[#991B1B]", step: 0 },
};

function ManagedConnector({ s }) {
  const m = s.managed || {};
  const st = STATES[m.state] || { label: m.state || "Unknown", tone: "bg-[#E7E5E4] text-[#44403C]", step: 0 };
  const steps = [
    "Click \"Claim Stripe account\" and sign in with the Masaya Rental Homes LLC Stripe login (not the Cleaning Services login). The sandbox attaches to that account.",
    "In the Rental Homes Stripe Dashboard, finish activation if anything is outstanding (business details, bank account) and activate Stripe Identity.",
    "Live credentials are injected automatically when the site is published — nobody copies or pastes a key.",
    "Live charges stay blocked until the owner approves (go-live checklist below).",
  ];
  return (
    <div className={card + " space-y-3"} data-testid="managed-connector-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-[#1C1917] font-medium flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Emergent-managed Stripe connector</p>
          <p className="text-xs text-[#78716C]">Dedicated Stripe sandbox for this site · account {m.sandbox_account_id || s.account?.id || "-"}{m.live_account_id ? ` · live ${m.live_account_id}` : ""}. Separate from Cleaning Services.</p>
        </div>
        <span data-testid="managed-state-badge" className={`rounded-full px-3 py-1 text-xs font-medium ${st.tone}`}>{st.label}</span>
      </div>
      {!m.available && <p data-testid="managed-unavailable" className="text-xs text-[#B45309]">Connector status unavailable: {m.reason}</p>}
      {m.available && m.state === "unclaimed" && m.onboarding_url && (
        <a href={m.onboarding_url} target="_blank" rel="noopener noreferrer" data-testid="managed-claim-link" className={btn + " text-xs inline-flex"}>
          Claim Stripe account for Masaya Rental Homes LLC <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {m.available && m.state === "unclaimed" && (
        <p className="text-xs text-[#78716C]">Claim link valid until {fmt(m.onboarding_url_expires_at)} (Stripe also emails it) · sandbox expires {fmt(m.sandbox_expires_at)} if never claimed.</p>
      )}
      <ol className="list-decimal pl-5 space-y-1 text-sm text-[#44403C]">
        {steps.map((t, i) => <li key={t} className={i < st.step ? "line-through text-[#A8A29E]" : ""}>{t}</li>)}
      </ol>
    </div>
  );
}

function FallbackSection({ s, busy, run }) {
  const [open, setOpen] = useState(s.key_source === "portal");
  return (
    <div className={card} data-testid="fallback-section">
      <button type="button" onClick={() => setOpen(!open)} data-testid="fallback-toggle" className="text-xs text-[#78716C] underline underline-offset-4">
        {open ? "Hide" : "Show"} emergency fallback: connect with your own restricted API key
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-[#B45309]">Only for emergencies. A key saved here overrides the Emergent-managed connector until you disconnect it.</p>
          <KeyForm s={s} busy={busy} run={run} />
          {s.key_source === "portal" && <WebhookSetup s={s} busy={busy} run={run} />}
        </div>
      )}
    </div>
  );
}

function ConnectionStatus({ s, onRefresh }) {
  const own = s.key_source === "portal" || s.key_source === "managed";
  const a = s.account || {};
  const live = s.mode === "live";
  return (
    <div className={card}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className={eyebrow}>Stripe connection</p>
          <p data-testid="stripe-account-name" className="mt-1 text-lg text-[#1C1917]">{own ? (a.business_name || "Unnamed Stripe account") : "Masaya Rental Homes LLC account not connected"}</p>
          {own && s.key_source === "portal" && <p data-testid="stripe-account-id" className="text-xs text-[#78716C]">{a.id} · {a.country} · {a.email || "no email on file"} · {s.key_type === "rk" ? "restricted" : "standard"} key ending {s.key_last4} · connected {fmt(s.connected_at)}</p>}
          {s.key_source === "managed" && <p data-testid="stripe-managed-note" className="text-xs text-[#78716C]">{a.id} · {a.country} · Emergent-managed connector (no keys to copy) · ending {s.key_last4}</p>}
          {!own && <p className="text-xs text-[#78716C]">{s.shared_preview_key ? "Preview is using Emergent's shared Stripe test key, not your account." : s.connected ? `Using the ${s.env_var} environment secret.` : "No Stripe key configured."}</p>}
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="stripe-mode-badge" className={`rounded-full px-3 py-1 text-xs font-medium ${live ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{s.mode === "test" ? "TEST MODE" : s.mode.toUpperCase().replace("_", " ")}</span>
          <button onClick={onRefresh} data-testid="stripe-refresh" className={ghost + " text-xs"}><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <p data-testid="stripe-mode-note" className="mt-3 text-xs text-[#78716C]">
        {live
          ? (s.live_approved ? "LIVE key connected and live payments approved: real cards are charged when guest testing mode is off." : "LIVE key connected. Real charges stay blocked until the owner approves live payments (STRIPE_LIVE_APPROVED) and guest testing mode is switched off.")
          : s.key_source === "managed" ? "Sandbox (test) credentials from the Emergent connector. Live credentials arrive automatically on publish after the account is claimed and activated; real charges stay blocked until the owner approves."
            : s.live_allowed ? "This environment accepts live keys. Public checkout and Instant Booking remain disabled until the go-live checklist is complete."
              : "Test mode only in this environment. Live keys are rejected here; connect them on the published site."}
      </p>
    </div>
  );
}

function KeyForm({ s, busy, run }) {
  const [key, setKey] = useState("");
  const live = s.live_allowed;
  const save = async (e) => {
    e.preventDefault();
    const res = await run("key", () => api.put("/admin/stripe/connection", { secret_key: key }), "Stripe key verified and saved");
    setKey("");
    return res;
  };
  return (
    <form onSubmit={save} className={card + " space-y-3"} data-testid="stripe-key-form" autoComplete="off">
      <p className="text-sm text-[#1C1917] font-medium flex items-center gap-2"><KeyRound className="w-4 h-4" /> {s.key_source === "portal" ? "Replace fallback secret key" : live ? "Fallback: your own live restricted key" : "Fallback: your own test secret key"}</p>
      <p className="text-xs text-[#78716C]">
        {live
          ? "Stripe Dashboard → Live mode → Developers → API keys → Restricted keys → reveal the key (rk_live_...). Paste it here only. It is checked with Stripe, encrypted before it is stored, and never shown again. Missing permissions are listed by the readiness check below."
          : "Stripe Dashboard → turn on Test mode → Developers → API keys → reveal the Secret key (sk_test_...). Paste it here only. It is checked with Stripe, encrypted before it is stored, and never shown again."}
      </p>
      <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={live ? "rk_live_... or sk_test_..." : "sk_test_..."} autoComplete="new-password" spellCheck={false}
        data-testid="stripe-key-input" className={inp + " font-mono"} />
      <div className="flex gap-2 flex-wrap">
        <button disabled={busy === "key" || key.trim().length < 20} data-testid="stripe-key-save" className={btn + " text-xs"}>
          {busy === "key" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & save key"}
        </button>
        {s.key_source === "portal" && (
          <button type="button" data-testid="stripe-disconnect" className={ghost + " text-xs"}
            onClick={() => window.confirm("Remove the fallback key? Masaya returns to the Emergent-managed connector.") && run("dc", () => api.delete("/admin/stripe/connection"), "Fallback key removed")}>Remove fallback key</button>
        )}
      </div>
    </form>
  );
}

function WebhookSetup({ s, busy, run }) {
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  const w = s.webhook || {};
  const url = `${window.location.origin}/api/stripe/webhook`;
  return (
    <div className={card + " space-y-3"} data-testid="stripe-webhook-panel">
      <p className="text-sm text-[#1C1917] font-medium flex items-center gap-2"><Webhook className="w-4 h-4" /> Webhook endpoint ({s.mode} mode)</p>
      <p className="text-xs text-[#78716C] break-all">Endpoint URL: <span data-testid="stripe-webhook-url" className="font-mono text-[#44403C]">{w.url || url}</span></p>
      <p data-testid="stripe-webhook-status" className="text-xs text-[#44403C]">
        Signing secret: {w.configured ? `stored securely (${w.secret_source}, ${w.livemode ? "live" : "test"} mode)` : "not configured"} · Last signed event: {w.last_event ? `${w.last_event.type} at ${fmt(w.last_event.received_at)}${w.last_event.ignored ? ` (ignored: ${w.last_event.ignored})` : ""}` : "none yet"}
        {w.ignored_livemode_events > 0 && <span data-testid="stripe-webhook-ignored" className="text-[#B45309]"> · {w.ignored_livemode_events} event(s) ignored because their mode did not match the connected key</span>}
      </p>
      <div className="flex gap-2 flex-wrap">
        <button disabled={!!busy} data-testid="stripe-webhook-create" className={btn + " text-xs"}
          onClick={() => run("wh", () => api.post("/admin/stripe/webhook", { origin_url: window.location.origin }), "Webhook endpoint created; signing secret stored")}>
          {busy === "wh" ? <Loader2 className="w-4 h-4 animate-spin" /> : w.endpoint_id ? "Recreate webhook endpoint" : "Create webhook endpoint"}
        </button>
        <button type="button" onClick={() => setShowManual(!showManual)} data-testid="stripe-webhook-manual-toggle" className={ghost + " text-xs"}>Enter signing secret manually</button>
      </div>
      {showManual && (
        <form className="flex gap-2" autoComplete="off" onSubmit={async (e) => { e.preventDefault(); await run("whs", () => api.put("/admin/stripe/webhook-secret", { webhook_secret: manual }), "Signing secret stored"); setManual(""); }}>
          <input type="password" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="whsec_..." autoComplete="new-password" data-testid="stripe-whsec-input" className={inp + " font-mono"} />
          <button disabled={!!busy || manual.length < 20} data-testid="stripe-whsec-save" className={btn + " text-xs"}>Save</button>
        </form>
      )}
    </div>
  );
}

function VerifyPanel({ s, busy, run }) {
  const v = s.last_verification;
  const a = v?.account || {};
  return (
    <div className={card + " space-y-3"} data-testid="stripe-verify-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-[#1C1917] font-medium flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Stripe readiness check</p>
          <p className="text-xs text-[#78716C]">Account approval, key permissions, webhook and Identity — checked live against Stripe.</p>
        </div>
        <button disabled={!!busy} data-testid="stripe-verify-btn" className={btn + " text-xs"} onClick={() => run("verify", () => api.post("/admin/stripe/verify"), "Readiness check complete")}>
          {busy === "verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run readiness check"}
        </button>
      </div>
      {v ? (
        <>
          <p data-testid="stripe-readiness-result" className={`text-sm font-medium ${v.ready ? "text-[#166534]" : "text-[#B45309]"}`}>
            {v.ready ? `Stripe ${v.mode} setup is ready` : "Blockers remain — see items marked below"} · checked {fmt(v.checked_at)}
          </p>
          <ul className="space-y-1.5 text-sm">
            {v.checks.map((c) => (
              <li key={c.check} data-testid={`stripe-check-${c.check.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex gap-2">
                <Ok ok={c.ok} />
                <span className="text-[#1C1917]">{c.check}{!c.ok && c.blocking === false && <span className="text-[11px] text-[#78716C]"> (recommended)</span>}</span>
                <span className="text-[#78716C] text-xs mt-0.5">{c.detail}</span>
              </li>
            ))}
          </ul>
          {v.permissions && (
            <div className="pt-2 border-t border-[#E7E5E4]">
              <p className="text-xs text-[#78716C] mb-1.5">Restricted-key permissions</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs" data-testid="stripe-permissions">
                {v.permissions.map((p) => (
                  <li key={p.slug} data-testid={`stripe-perm-${p.slug}`} className="flex gap-1.5">
                    <Ok ok={p.status === "ok"} />
                    <span className={p.status === "ok" ? "text-[#1C1917]" : "text-[#92400E]"}>{p.permission}{!p.required && <span className="text-[#78716C]"> (optional)</span>}</span>
                    {p.status !== "ok" && <span className="text-[#78716C]">— {p.detail}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {a.id && v.mode === "live" && (
            <p className="text-[11px] text-[#78716C]">Account {a.id} · {a.business_type || "business"} · MCC {a.mcc || "not set"} {a.mcc_label && `(${a.mcc_label})`} · descriptor {a.statement_descriptor || "not set"} · currency {a.default_currency?.toUpperCase() || "?"}</p>
          )}
        </>
      ) : <p className="text-xs text-[#78716C]">Not checked yet.</p>}
    </div>
  );
}

function TestCheckout({ s, busy, run }) {
  const [scenario, setScenario] = useState("exempt");
  const create = async () => {
    const d = await run("tc", () => api.post("/admin/stripe/test-checkout", { origin_url: window.location.origin, tax_scenario: scenario }), "Test checkout created");
    if (d?.checkout_url) window.open(d.checkout_url, "_blank", "noopener");
  };
  return (
    <div className={card + " space-y-3"} data-testid="stripe-test-checkout-panel">
      <p className="text-sm text-[#1C1917] font-medium">Test a monthly furnished-room checkout</p>
      <p className="text-xs text-[#78716C]">Creates one Stripe-hosted test Checkout for a 30-night stay (first month's rent, management fee, one-time cleaning fee, taxes; no deposit). No reservation is created and no calendar dates are held. Pay with card 4242 4242 4242 4242, any future date, any CVC.</p>
      <div className="flex gap-2 flex-wrap items-center">
        <select value={scenario} onChange={(e) => setScenario(e.target.value)} data-testid="stripe-test-scenario" className={inp + " w-auto"}>
          <option value="exempt">Verified exempt (taxes $0.00)</option>
          <option value="taxable">Not exempt (taxes added)</option>
        </select>
        <button disabled={!!busy} onClick={create} data-testid="stripe-test-checkout-btn" className={btn + " text-xs"}>
          {busy === "tc" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open test checkout <ExternalLink className="w-3.5 h-3.5" /></>}
        </button>
      </div>
      {(s.test_runs || []).map((r) => (
        <div key={r.id} data-testid={`stripe-test-run-${r.id}`} className="rounded-lg border border-[#E7E5E4] p-3 text-xs text-[#44403C] flex justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[#1C1917] text-sm">{r.room_name} · {r.check_in} → {r.check_out} · {r.tax_scenario}</p>
            <p>Rent {money(r.lines?.rent)} · Mgmt {money(r.lines?.management_fee)} · Cleaning {money(r.lines?.cleaning_fee)} · Texas {money(r.lines?.texas_tax)} · Occupancy {money(r.lines?.occupancy_tax)} · Deposit {money(r.security_deposit)}</p>
            <p>Created {fmt(r.created_at)}{r.failed_attempts ? ` · ${r.failed_attempts} declined attempt(s): ${r.last_failure}` : ""}</p>
          </div>
          <div className="text-right space-y-1">
            <p className="text-sm text-[#1C1917]">{money(r.amount)}</p>
            <p data-testid={`stripe-test-run-status-${r.id}`} className="uppercase tracking-wide">{r.status.replace("_", " ")}</p>
            {r.status === "paid" && (
              <button disabled={!!busy} data-testid={`stripe-test-refund-${r.id}`} className={ghost + " text-xs"}
                onClick={() => run("rf", () => api.post(`/admin/stripe/test-runs/${r.id}/refund`), "Test refund requested")}><Undo2 className="w-3.5 h-3.5" /> Refund</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function GoLiveChecklist({ refreshKey }) {
  const [c, setC] = useState(null);
  const load = async () => { try { setC((await api.get("/admin/stripe/go-live-checklist")).data); } catch (e) { toast.error(extractErr(e)); } };
  useEffect(() => { load(); }, [refreshKey]);
  if (!c) return null;
  const groups = [["stripe", "Stripe"], ["env", "Environment secrets"], ["policy", "Booking policy & content"], ["switch", "Final activation switches (owner approval required)"]];
  const done = c.items.filter((i) => i.ok).length;
  return (
    <div className={card + " space-y-3"} data-testid="go-live-checklist">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-[#1C1917] font-medium flex items-center gap-2"><Lock className="w-4 h-4" /> Go-live checklist</p>
          <p className="text-xs text-[#78716C]">Status is read-only; the two owner switches above are the only way to go live. Real guests are charged only when every item is green, live payments are approved and testing mode is off.</p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="go-live-progress" className="text-xs text-[#78716C]">{done}/{c.items.length} complete</span>
          <button onClick={load} data-testid="go-live-refresh" className={ghost + " text-xs"}><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <ActivationControls c={c} onChange={load} />
      {groups.filter(([kind]) => c.items.some((i) => i.kind === kind)).map(([kind, label]) => (
        <div key={kind}>
          <p className={eyebrow + " mb-1.5"}>{label}</p>
          <ul className="space-y-1.5 text-sm">
            {c.items.filter((i) => i.kind === kind).map((i) => (
              <li key={i.key} data-testid={`go-live-${i.key}`} className="flex gap-2">
                <Ok ok={i.ok} />
                <span className="text-[#1C1917]">{i.label}</span>
                <span className="text-[#78716C] text-xs mt-0.5">{i.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ActivationControls({ c, onChange }) {
  const { user } = usePortalAuth();
  const [busy, setBusy] = useState("");
  const k = c.controls || {};
  if (user?.role !== "admin") return null;
  const blockingOpen = c.items.filter((i) => !i.ok && i.kind !== "switch").map((i) => i.label);
  const act = async (name, fn, ok) => {
    setBusy(name);
    try { await fn(); toast.success(ok); await onChange(); } catch (e) { toast.error(extractErr(e)); } finally { setBusy(""); }
  };
  const approve = () => {
    const typed = window.prompt("This allows real guests' cards to be charged once guest testing mode is switched off.\n\nType APPROVE LIVE PAYMENTS to confirm:");
    if (typed === null) return;
    act("approve", () => api.put("/admin/booking/payment-settings/live-approval", { approved: true, confirmation: typed }), "Live payments approved");
  };
  const revoke = () => window.confirm("Revoke live-payment approval? New payment links will be blocked immediately.") &&
    act("revoke", () => api.put("/admin/booking/payment-settings/live-approval", { approved: false }), "Live-payment approval revoked");
  const setTesting = (on) => {
    const msg = on ? "Switch guest testing mode back ON? Public checkout becomes mocked again and no payment links can be sent."
      : `Switch guest testing mode OFF?\n\nReal reservations and payment links become possible${c.mode === "live" ? " and REAL cards will be charged" : " (test credentials, no real charges)"}.${blockingOpen.length ? `\n\nOpen checklist items: ${blockingOpen.join("; ")}` : ""}`;
    if (!window.confirm(msg)) return;
    act("mode", () => api.put("/admin/booking/payment-settings/mode", { guest_testing_mode: on }), on ? "Guest testing mode ON" : "Guest testing mode OFF");
  };
  return (
    <div className="rounded-xl border border-[#E7E5E4] bg-[#FAFAF9] p-4 space-y-3" data-testid="activation-controls">
      <p className={eyebrow}>Final activation (owner only)</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm text-[#1C1917]">1 · Live-payment approval: <span data-testid="activation-approval-state" className={k.live_approved ? "text-[#166534] font-medium" : "text-[#92400E] font-medium"}>{k.live_approved ? "approved" : "not approved"}</span></p>
          <p className="text-xs text-[#78716C]">{k.live_approved ? `by ${k.approval_by || "environment"} · ${fmt(k.approval_at)}` : k.can_approve_live ? "Live credentials active and readiness check passed — you can approve." : c.mode === "live" ? "Run the readiness check in live mode and clear its blockers first." : "Available on the published site once live credentials are active."}</p>
        </div>
        {k.live_approved
          ? <button disabled={!!busy} onClick={revoke} data-testid="activation-revoke" className={ghost + " text-xs"}>Revoke approval</button>
          : <button disabled={!!busy || !k.can_approve_live} onClick={approve} data-testid="activation-approve" className={btn + " text-xs"}>{busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve live payments"}</button>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm text-[#1C1917]">2 · Guest testing mode: <span data-testid="activation-testing-state" className={k.guest_testing_mode ? "text-[#92400E] font-medium" : "text-[#166534] font-medium"}>{k.guest_testing_mode ? "ON (public checkout mocked)" : "OFF (live bookings enabled)"}</span></p>
          <p className="text-xs text-[#78716C]">{k.mode_changed_by ? `Last changed by ${k.mode_changed_by} · ${fmt(k.mode_changed_at)}` : "Switch off only after step 1 and when every item below is green."}</p>
        </div>
        <button disabled={!!busy} onClick={() => setTesting(!k.guest_testing_mode)} data-testid="activation-testing-toggle" className={(k.guest_testing_mode ? btn : ghost) + " text-xs"}>
          {busy === "mode" ? <Loader2 className="w-4 h-4 animate-spin" /> : k.guest_testing_mode ? "Switch testing mode OFF" : "Switch testing mode ON"}
        </button>
      </div>
    </div>
  );
}

function SettingsGuide({ live }) {
  const items = live ? [
    "Live mode ON in the Stripe Dashboard (top-right toggle) whenever you copy keys.",
    "Restricted live key permissions: Account Read · Checkout Sessions Write · PaymentIntents Read · Charges Read · Refunds Write · Identity VerificationSessions Write · Webhook Endpoints Write (lets the Portal create the endpoint) · Events Read (optional).",
    "Settings → Business → Public details: business name Masaya Rental Homes LLC, statement descriptor (e.g. MASAYA RENTAL HOMES), industry Lodging / Real estate rentals.",
    "Settings → Emails: enable 'Successful payments' and 'Refunds' so Stripe sends card receipts; Masaya sends its own branded confirmation separately.",
    "Stripe Identity: Dashboard → Identity → activate in live mode (per-verification fee applies).",
    "Payment methods: Settings → Payment methods → Cards enabled. Branding: Settings → Branding for Checkout logo and colors.",
  ] : [
    "Claim the connector with the Masaya Rental Homes LLC Stripe login — this keeps it separate from Cleaning Services.",
    "Business details: Settings → Business → Public details: business name Masaya Rental Homes LLC, statement descriptor (e.g. MASAYA RENTAL HOMES), industry Lodging / Real estate rentals.",
    "Stripe Identity: Dashboard → Identity → Get started and accept the terms (needed before guest ID checks work).",
    "Settings → Emails: enable 'Successful payments' and 'Refunds' so Stripe sends card receipts (Masaya sends its own branded confirmation too).",
    "Payment methods: Settings → Payment methods → keep Cards enabled. Branding (optional): Settings → Branding for the Checkout logo and colors.",
  ];
  return (
    <div className={card} data-testid="stripe-settings-guide">
      <p className={eyebrow + " mb-3"}>Stripe settings to enable ({live ? "live" : "test"} mode)</p>
      <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#44403C]">{items.map((t) => <li key={t}>{t}</li>)}</ol>
    </div>
  );
}
